// @fileoverview : 생산관리 > 생산계획별생산현황

import { customFunctions } from "@/config/customFunction";
import { itemTypeColor } from "@/constants/items";
import { EquipmentDetailForm } from "@/features/equipment/components/form/EquipmentDetailForm";
import { ItemsDetailForm } from "@/features/item/components/form/ItemsDetailForm";
import { ItemCategoryDetailForm } from "@/features/itemCategory/form/DetailForm";
import { LocationDetailForm } from "@/features/location/detail/LocationDetailForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { OperationDetailForm } from "@/features/operations/components/form/operationDetailForm";
import { ProductionPlanViewForm } from "@/features/productionPlan/view";
import { RoutingDetailForm } from "@/features/routing/components/form/RoutingDetailForm";
import { useStandardLayout } from "@/features/standardLayout/Context";
import { Filter } from "@/features/standardLayout/Filter";
import { LinkAnchor } from "@/features/standardLayout/LinkAnchor";
import { ListTable } from "@/features/standardLayout/ListTable";
import { PaginationBox } from "@/features/standardLayout/PaginationBox";
import { SearchBox } from "@/features/standardLayout/SearchBox";
import { Sorter } from "@/features/standardLayout/Sorter";
import {
  flexGrowable,
  flexStable,
  StandardLayoutTableWrapper,
} from "@/features/standardLayout/Styles";
import { UsersDetailForm } from "@/features/users/components/form/UsersDetailForm";
import { WorkView } from "@/features/work/WorkView/WorkView";
import { DateHeader } from "@/features/workLog/DateHeader";
import { ActionHeader } from "@/features/workLog/WorkLogActionHeader";
import { setToLocaleString } from "@/utils/unitMark";
import { setWorkStatus, statusObj, trackingStatusColor } from "@/utils/workStatus";
import { Avatar, Badge, Button, Chip, Flex, Group, Text, Tooltip } from "@mantine/core";
import {
  ItemsGet200ResponseRowsInnerItemTypeEnum,
  WorkLogsGet200Response,
  WorkLogsGet200ResponseRowsInnerWorkLogTypeEnum,
  WorkLogsWorksGet200ResponseRowsInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconFilterPlus } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Column } from "react-data-grid";
import { useTranslation } from "react-i18next";

// 작업 상태 화면에 표시 갯수 threshold
const MAX_DISPLAY = 10;

export const WorkLogPage = () => {
  const { openModal } = useModal();
  const { query, setQuery, startDate, endDate } = useStandardLayout();
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const workLogTypeQuery = query.find((q) => q && "workLogType" in q);
  const workLogTypeQueryData = workLogTypeQuery?.workLogType;

  const [chipState, setChipState] = useState(() => {
    // query 배열에서 workLogType 키를 가진 객체를 찾고, 그 값을 반환하거나 workStatus의 키 목록으로 초기화

    const response: string[] = [];

    if (workLogTypeQueryData && Array.isArray(workLogTypeQueryData)) {
      const data: string[] =
        workLogTypeQueryData.length > 0 ? workLogTypeQueryData : ["INPUT", "PRODUCTION"];
      response.push(...data);
    }

    return response;
  });

  // workLogType 기본값을 유지하기 위한 useEffect
  useEffect(() => {
    if (
      !workLogTypeQueryData ||
      !Array.isArray(workLogTypeQueryData) ||
      workLogTypeQueryData.length === 0
    ) {
      setChipState(["INPUT", "PRODUCTION"]);
    }
  }, [workLogTypeQueryData]);

  useEffect(() => {
    setQuery((prev) => {
      // 기존 query에서 workLogType, createdAt, $or을 제외한 나머지 조건들 유지
      const filteredQuery = prev.filter(
        (q) => !("workLogType" in q) && !("createdAt" in q) && !("$or" in q),
      );

      // 새로운 조건 추가
      const newQuery = [
        ...filteredQuery,
        {
          workLogType: chipState,
        },
        {
          createdAt: {
            $gte: dayjs(startDate).startOf("day").toISOString(),
            $lte: dayjs(endDate).endOf("day").toISOString(),
          },
        },
        {
          $or: [{ description: { $eq: null } }, { description: { $ne: "ACTION" } }],
        },
      ];

      return newQuery;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chipState, startDate, endDate]);

  const columns: readonly Column<WorkLogsWorksGet200ResponseRowsInner>[] = [
    {
      key: "id",
      name: t("workLog.id"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return <Flex justify="flex-end">{formatterProps.row.id}</Flex>;
      },
    },
    ...(customFunctions.ADD_WORK_LOG_ERP_WORK_ORDER_COLUMN
      ? [
          {
            key: "erpWorkOrderNo",
            name: t("workLog.erpWorkOrderNo"),
            sortable: true,
            resizable: true,
            width: 140,
            formatter: (formatterProps: any) => {
              return <Flex justify="flex-end">{formatterProps.row.erpWorkOrderNo}</Flex>;
            },
          },
        ]
      : []),
    {
      key: "createdAt",
      name: t("workLog.createdAt"),
      width: 160,
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <Flex h="100%" align="center">
            {row.createdAt !== null ? dayjs(row.createdAt).format("YYYY-MM-DD HH:mm:ss") : "-"}
          </Flex>
        );
      },
    },
    {
      key: "equipment.name",
      name: t("workLog.equipment"),
      width: 160,
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <LinkAnchor
            onClick={() => {
              openModal(
                <EquipmentDetailForm equipmentCode={row.equipmentCode} />,
                null,
                `${t("common.detail", { item: `${t("equipment.equipment")}` })}`,
              );
            }}
          >
            {row.equipmentCode}({row?.equipment?.name})
          </LinkAnchor>
        );
      },
    },
    {
      key: "routingCode",
      name: t("routing.code"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <LinkAnchor
            onClick={() => {
              openModal(
                <RoutingDetailForm routingCode={row.routingCode} />,
                null,
                `${t("common.detail", { item: `${t("routing.routing")}` })}`,
              );
            }}
          >
            {row.routingCode}
          </LinkAnchor>
        );
      },
    },
    {
      key: "operationCode",
      name: t("operation.code"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <LinkAnchor
            onClick={() => {
              openModal(
                <OperationDetailForm operationCode={row.operationCode} />,
                null,
                `${t("common.detail", { item: `${t("operation.operation")}` })}`,
              );
            }}
          >
            {row.operationCode}
          </LinkAnchor>
        );
      },
    },
    {
      key: "workLogType",
      name: t("workLog.type"),
      width: 110,
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        const badgeColor =
          trackingStatusColor[row.workLogType as WorkLogsGet200ResponseRowsInnerWorkLogTypeEnum] ||
          "defaultColor";
        return (
          <Badge size="lg" color={badgeColor}>
            {t(setWorkStatus(row.workLogType))}
            {row.workLogType === "TIME_TRACKING_PAUSE" && `(${row.downtimeReasonName})`}
            {row.workLogType === "DEFECT" && `(${row.defectName})`}
          </Badge>
        );
      },
    },
    {
      key: "quantity",
      name: t("inventory.quantity"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <Flex justify="flex-end">
            {row.quantity !== null
              ? row.workLogType === "CONSUME_IDENTIFIED"
                ? setToLocaleString(row.quantity ? -row.quantity : "")
                : setToLocaleString(row.quantity)
              : "0"}{" "}
            {row.unitText}
          </Flex>
        );
      },
    },
    {
      key: "totalQuantity",
      name: t("workLog.totalQuantity"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <Flex justify="flex-end">
            {["PRODUCTION", "LOSS", "CANCEL_PRODUCTION", "CANCEL_LOSS"].includes(
              row.workLogType as string,
            )
              ? `${setToLocaleString(row?.currentLotSummary?.totalQuantity)} ${row.unitText}`
              : ""}
          </Flex>
        );
      },
    },
    {
      key: "itemCode",
      name: t("item.code"),
      sortable: true,
      resizable: true,
      formatter: ({ row }: any) => {
        return (
          <LinkAnchor
            onClick={() => {
              openModal(
                <ItemsDetailForm
                  itemCode={
                    row.logData?.incoming
                      ? row.logData?.incoming.itemCode
                      : row.logData?.outgoing?.itemCode
                  }
                />,
                null,
                `${t("common.detail", { item: `${t("item.item")}` })}`,
              );
            }}
          >
            {row.logData?.incoming
              ? row.logData?.incoming.itemCode
              : row.logData?.outgoing?.itemCode}
          </LinkAnchor>
        );
      },
    },
    {
      key: "itemData.name",
      name: t("item.name"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <Flex align="center">
            {row.logData?.incoming
              ? row.logData?.incoming.itemData?.name
              : row.logData?.outgoing?.itemData?.name}
          </Flex>
        );
      },
    },
    {
      key: "itemData.spec",
      name: t("item.spec"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <Flex align="center">
            {row.logData?.incoming
              ? row.logData?.incoming.itemData?.spec
              : row.logData?.outgoing?.itemData?.spec}
          </Flex>
        );
      },
    },
    {
      key: "itemData.itemCategoryName",
      name: t("category.category"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <LinkAnchor
            onClick={() => {
              openModal(
                <ItemCategoryDetailForm ItemCategoryName={row.itemData?.itemCategoryName} />,
                null,
                `${t("common.detail", { item: `${t("category.category")}` })}`,
              );
            }}
          >
            {row.itemData?.itemCategoryName}
          </LinkAnchor>
        );
      },
    },
    {
      key: "itemData.itemType",
      name: t("item.type"),
      sortable: true,
      resizable: true,

      formatter: ({ row }) => {
        const badgeColor =
          itemTypeColor[row.itemData?.itemType as ItemsGet200ResponseRowsInnerItemTypeEnum];
        return (
          <Badge size="lg" color={badgeColor}>
            {t(row.itemData?.itemType as ItemsGet200ResponseRowsInnerItemTypeEnum)}
          </Badge>
        );
      },
    },
    {
      key: "itemData.unitText",
      name: t("item.unit"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return <Flex align="center">{row.itemData?.unitText}</Flex>;
      },
    },
    {
      key: "lotData.name",
      name: t("lot.name"),
      sortable: true,
      resizable: true,
      width: 180,
      formatter: ({ row }) => {
        return (
          <>
            {row.workLogType === "CONSUME_IDENTIFIED" ? (
              <div>
                <span>{row.logData?.outgoing?.lotData?.name} </span>
              </div>
            ) : row.logData?.incoming ? (
              <div>
                <span>{row.logData?.incoming?.lotData?.name} </span>
              </div>
            ) : (
              <div>
                <span>{row.logData?.outgoing?.lotData?.name} </span>
              </div>
            )}
          </>
        );
      },
    },
    {
      key: "lotData.expiration",
      name: t("lot.lotExpiration"),
      sortable: true,
      resizable: true,
      width: 160,
      formatter: ({ row }) => {
        return (
          <>
            {row.logData?.incoming ? (
              <div>
                <span>
                  {row.logData?.incoming?.lotData?.expiration
                    ? dayjs(row.logData?.incoming?.lotData.expiration).format("YYYY-MM-DD HH:mm:ss")
                    : ""}
                </span>
              </div>
            ) : (
              <div>
                <span>
                  {row.logData?.outgoing?.lotData?.expiration
                    ? dayjs(row.logData?.outgoing?.lotData.expiration).format("YYYY-MM-DD HH:mm:ss")
                    : ""}
                </span>
              </div>
            )}
          </>
        );
      },
    },
    {
      key: "locationData.name",
      name: t("workLog.location"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <LinkAnchor
            onClick={() => {
              openModal(
                <LocationDetailForm
                  locationCode={
                    row?.logData?.incoming?.locationCode || row?.logData?.outgoing?.locationCode
                  }
                />,
                null,
                `${t("common.detail", { item: `${t("location.location")}` })}`,
              );
            }}
          >
            {row.logData?.incoming
              ? row.logData?.incoming.locationData?.name
              : row.logData?.outgoing?.locationData?.name}
          </LinkAnchor>
        );
      },
    },
    {
      key: "workId",
      name: t("workLog.workId"),
      width: 30,
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <Flex h="100%" justify="flex-end" align="center">
            <LinkAnchor
              onClick={() => {
                openModal(
                  <WorkView workId={row.workId} />,
                  null,
                  `${t("common.detail", { item: `${t("work.workOrder")}` })}`,
                );
              }}
            >
              {row.workId}
            </LinkAnchor>
          </Flex>
        );
      },
    },
    {
      key: "productionPlanId",
      name: t("productionPlan.id"),
      width: 30,
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <Flex h="100%" justify="flex-end" align="center">
            <LinkAnchor
              onClick={() => {
                openModal(
                  <ProductionPlanViewForm ProductionPlanId={row.productionPlanId} />,
                  null,
                  t("productionPlan.productionPlan"),
                );
              }}
            >
              {row.productionPlanId}
            </LinkAnchor>
          </Flex>
        );
      },
    },
    {
      key: "creatorUserName",
      name: t("workLog.worker"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <LinkAnchor
            onClick={() => openModal(<UsersDetailForm UserCode={row?.creatorUserCode} />, null, "")}
          >
            {row?.creatorUserName}
          </LinkAnchor>
        );
      },
    },
  ];

  return (
    <Flex w="100%" h="100%" gap="xs" direction="column">
      <Flex direction="column" p="sm" justify="space-between" rowGap="md">
        <ActionHeader />
        <DateHeader />
      </Flex>
      <Flex bg="white" px="sm" direction="column" gap="xs">
        <Text fz="xl" fw={600}>
          {t("common.list", { item: `${t("workLog.workLog")}` })}
        </Text>
        <Text fz="xs">{t("common.subTitle", { item: `${t("workLog.workLog")}` })}</Text>
      </Flex>
      <Flex justify="space-between" align="flex-start" px="sm">
        <Flex wrap="wrap" styles={flexGrowable}>
          <Flex gap="xs" align="center" h="100%" wrap="wrap" direction="row">
            <Tooltip label={t("common.filterDescription")}>
              <Avatar color="blue" variant="outline">
                <IconFilterPlus />
              </Avatar>
            </Tooltip>
            <Flex direction="column" gap="xs" wrap="wrap">
              <Filter />
              <Sorter />
            </Flex>
          </Flex>
        </Flex>
        <Flex styles={flexStable}>
          <SearchBox />
        </Flex>
      </Flex>
      <Chip.Group multiple value={chipState}>
        <Group px="sm">
          {Object.keys(statusObj).map((key: string, index: number) => {
            return (
              (isExpanded || index < MAX_DISPLAY) && (
                <Chip
                  variant="filled"
                  size="sm"
                  radius="xs"
                  color={trackingStatusColor[key]}
                  key={key}
                  value={key}
                  checked={chipState && chipState.includes(key)}
                  onClick={(e) => {
                    const targetValue = e.currentTarget.value;
                    if (chipState.includes(targetValue)) {
                      setChipState(chipState.filter((chip: string) => chip !== targetValue));
                    } else {
                      setChipState([...chipState, targetValue]);
                    }
                  }}
                >
                  {t(statusObj[key])}
                </Chip>
              )
            );
          })}
          {Object.keys(statusObj).length > MAX_DISPLAY && (
            <Button
              onClick={() => setIsExpanded(!isExpanded)}
              radius="xs"
              size="xs"
              fz="sm"
              variant="filled"
              color={isExpanded ? "gray" : "blue"}
            >
              {isExpanded ? t("workLog.hide") : t("workLog.see")}
            </Button>
          )}
        </Group>
      </Chip.Group>
      <Flex w="100%" h="100%" p="sm" justify="space-between" gap="md" direction="column">
        <StandardLayoutTableWrapper>
          <ListTable<WorkLogsGet200Response, WorkLogsWorksGet200ResponseRowsInner>
            columns={columns}
          />
        </StandardLayoutTableWrapper>
        <PaginationBox<WorkLogsGet200Response> />
      </Flex>
    </Flex>
  );
};
